import { getColor, mode } from '@chakra-ui/theme-tools';

const parts = ['field', 'addon'];

const baseStyle = {
  field: {
    fontWeight: 'semibold',
    width: 'full',
    outline: 0,
    position: 'relative',
    appearance: 'none',
    transition: 'all 0.2s',
  },
};

const size = {
  lg: {
    fontSize: 'lg',
    pl: 4,
    pr: 4,
    h: 12,
    borderRadius: 'md',
  },

  md: {
    fontSize: 'md',
    pl: 4,
    pr: 4,
    h: 10,
    borderRadius: 'md',
  },

  sm: {
    fontSize: 'sm',
    pl: 3,
    pr: 3,
    h: 8,
    borderRadius: 'sm',
  },
};

const sizes = {
  lg: {
    field: size.lg,
    addon: size.lg,
  },
  md: {
    field: size.md,
    addon: size.md,
  },
  sm: {
    field: size.sm,
    addon: size.sm,
  },
};

function getDefaults(props: Dict) {
  const { focusBorderColor: fc, errorBorderColor: ec } = props;
  return {
    focusBorderColor: fc || mode('primary', 'blue.300')(props),
    errorBorderColor: ec || mode('red.500', 'red.300')(props),
  };
}

function variantOutline(props: Dict) {
  const { theme } = props;
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);

  return {
    field: {
      border: '1px solid',
      borderColor: 'inherit',
      bg: 'inherit',
      _hover: {
        borderColor: mode('gray.300', 'whiteAlpha.400')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _focus: {
        zIndex: 1,
        borderColor: getColor(theme, fc),
        boxShadow: `0 0 0 1px ${getColor(theme, fc)}`,
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: `0 0 0 1px ${getColor(theme, ec)}`,
      },
    },
    addon: {
      border: '1px solid',
      borderColor: mode('inherit', 'whiteAlpha.50')(props),
      bg: mode('gray.100', 'whiteAlpha.300')(props),
    },
  };
}
function variantUnderlined(props: Dict) {
  const { theme } = props;
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);

  return {
    field: {
      borderBottom: 'thin solid',
      bg: 'inherit',
      color: 'blue.500',
      paddingBottom: 1,
      margin: 1,
      _hover: {
        borderColor: mode('gray.300', 'whiteAlpha.400')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _focus: {
        zIndex: 1,
        boxShadow: `0 0 0 1px ${getColor(theme, fc)}`,
      },
      _invalid: {
        borderColor: getColor(theme, ec),
        boxShadow: `0 0 0 1px ${getColor(theme, ec)}`,
      },
    },
    addon: {
      border: '1px solid',
      borderColor: mode('inherit', 'blue.500')(props),
      bg: mode('gray.100', 'whiteAlpha.300')(props),
    },
  };
}
function variantFilled(props: Dict) {
  const { theme } = props;
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);

  return {
    field: {
      border: '2px solid',
      borderColor: 'transparent',
      bg: mode('gray.100', 'whiteAlpha.50')(props),
      _hover: {
        bg: mode('gray.200', 'whiteAlpha.100')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _focus: {
        bg: 'transparent',
        borderColor: getColor(theme, fc),
      },
      _invalid: {
        borderColor: getColor(theme, ec),
      },
    },
    addon: {
      border: '2px solid',
      borderColor: 'transparent',
      bg: mode('gray.100', 'whiteAlpha.50')(props),
    },
  };
}

function variantFlushed(props: Dict) {
  const { theme } = props;
  const { focusBorderColor: fc, errorBorderColor: ec } = getDefaults(props);

  return {
    field: {
      borderBottom: '1px solid inherit',
      borderRadius: 0,
      pl: 0,
      pr: 0,
      bg: 'transparent',
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _focus: {
        borderColor: getColor(theme, fc),
        boxShadow: `0px 1px 0px 0px ${getColor(theme, fc)}`,
      },
      _invalid: {
        borderColor: getColor(theme, ec),
      },
    },
    addon: {
      borderBottom: '2px solid',
      borderColor: 'inherit',
      borderRadius: 0,
      paddingX: 0,
      bg: 'transparent',
    },
  };
}

const variantUnstyled = {
  field: {
    bg: 'transparent',
    pl: 0,
    pr: 0,
    height: 'auto',
  },
  addon: {
    bg: 'transparent',
    pl: 0,
    pr: 0,
    height: 'auto',
  },
};

const ROO_FOCUS_BORDER_COLOR = 'gray.400';

function rooUnstyled(props: Dict) {
  const { theme } = props;
  const { errorBorderColor: ec } = getDefaults(props);
  const fc = ROO_FOCUS_BORDER_COLOR;
  return {
    field: {
      shadow: 'base',
      border: '1px',
      borderColor: 'transparent',
      fontWeight: 'semibold',
      color: 'primary',
      display: 'block',
      px: { base: 4, md: 6 },
      pt: 8,
      pb: 2,
      m: 0,
      borderRadius: 'lg',
      fontSize: 'md',
      appearance: 'none',
      bg: 'white',
      height: 'auto',
      _placeholder: {
        fontWeight: 'normal',
      },
      _hover: {
        borderColor: mode('gray.300', 'whiteAlpha.400')(props),
      },
      _readOnly: {
        boxShadow: 'none !important',
        userSelect: 'all',
      },
      _focus: {
        zIndex: 1,
        borderColor: getColor(theme, fc),
        boxShadow: `0 0 0 1px ${getColor(theme, fc)}`,
      },
      _disabled: {
        opacity: 0.4,
        cursor: 'not-allowed',
      },
      _invalid: {
        color: getColor(theme, ec),
        boxShadow: `0 0 0 1px ${getColor(theme, ec)}`,
        borderColor: getColor(theme, ec),
      },
    },
    addon: {
      bg: 'transparent',
      pl: 0,
      pr: 0,
      height: 'auto',
    },
  };
}

const variants = {
  outline: variantOutline,
  filled: variantFilled,
  flushed: variantFlushed,
  unstyled: variantUnstyled,
  underlined: variantUnderlined,
  roo: rooUnstyled,
};

const defaultProps = {
  size: 'md',
  variant: 'roo',
};

export default {
  defaultProps,
  parts,
  baseStyle,
  sizes,
  variants,
};
